import React, { useEffect, useState } from 'react';

import { Box } from '../box/box';
import { cardSurfaceSx, cardTitleSx } from '../style/card';
import { useTheme } from '../styles/styles';

interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  triggerOpen?: string | number | null;
  // Controls on the right of the header (e.g. a columns button); shown only
  // while the section is open.
  headerRight?: React.ReactNode;
}

export const CollapsibleSection = ({
  title,
  children,
  defaultOpen = false,
  triggerOpen,
  headerRight,
}: CollapsibleSectionProps) => {
  const [open, setOpen] = useState(defaultOpen);
  const theme = useTheme();

  useEffect(() => {
    if (triggerOpen) {
      setOpen(true);
    }
  }, [triggerOpen]);

  return (
    <Box
      sx={{
        ...cardSurfaceSx(theme),
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((prev) => !prev);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          minHeight: 32,
          padding: open ? '22px 16px 14px 20px' : '18px 16px 18px 20px',
          boxSizing: 'content-box',
          cursor: 'pointer',
          borderBottom: open
            ? `1px solid ${theme.custom?.tableTitleDivider}`
            : undefined,
          '&:hover .collapsible-chevron': { color: theme.custom?.brandNavy },
        }}
      >
        <Box
          component="span"
          sx={{
            minWidth: 0,
            ...cardTitleSx(theme),
          }}
        >
          {title}
        </Box>
        <Box
          component="svg"
          viewBox="0 0 16 16"
          aria-hidden
          className="collapsible-chevron"
          sx={{
            width: 14,
            height: 14,
            flexShrink: 0,
            fill: 'none',
            stroke: 'currentColor',
            strokeWidth: 1.8,
            color: theme.custom?.iconButton,
            transform: open ? 'none' : 'rotate(-90deg)',
            transition: 'transform 150ms',
          }}
        >
          <path d="m4 6 4 4 4-4" />
        </Box>
        <Box sx={{ flex: 1 }} />
        {open && headerRight && (
          // Clicks in these controls (and their pop-ups, which bubble through
          // React) must not open/close the section.
          <Box
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            sx={{ display: 'flex' }}
          >
            {headerRight}
          </Box>
        )}
      </Box>

      {open && (
        <Box
          flexGrow={1}
          width="100%"
          sx={{ '&:has(.MuiDataGrid-root)': { paddingBottom: '8px' } }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
};
